import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { overviewWidgetsPermissionsArray } from '../../../App.util';
import { useThemeContext } from '../../../global-state/themeContext';
import { AdditionalPermissions, Profile } from '../../../global-state/types';
import { useUser } from '../../../hooks';
import { useProfile } from '../../../hooks/useProfile';
import {
  getPermissionTranslationData,
  permissionGroupings,
  UserPermissionType,
} from '../../../hooks/useUserPermissions';
import { Icon, Icons, Typography } from '../../../stories/atoms';
import {
  ColumnFlexContainer,
  ContainerV2,
  RowFlexContainer,
} from '../../../stories/atoms/containerV2/ContainerV2';
import { Button, Checkbox, IconWithText } from '../../../stories/molecules';
import { HeaderRowItem, Table } from '../../../stories/molecules/tablev2/Table';
import { commonTranslations, permissionsTranslations } from '../../../translations';
import { getSuamRoles, isFailureResponse, isSupportedSuamRole, RoleMapping } from '../../../utils';
import { removeDuplicateCharacters } from '../../../utils/formatter';
import {
  canEditEmployeePermissions,
  getEmployeePermissions,
  isEditPermissionDisabled,
  PERMISSION_ACTIONS,
  PERMISSION_TYPE_MAP,
  PermissionConfig,
  PermissionTableRow,
} from './utils';

type PermissionsTableProps = {
  handleBackToMain: () => void;
  config: PermissionConfig;
  employee?: Profile;
  handleSubmit?: (isSuccess: boolean, type: string) => void;
};

const PermissionIcon: FC<{ hasPermission: boolean; testId: string }> = ({ hasPermission, testId }) => {
  const { mode } = useThemeContext();
  const iconColor =
    mode === 'light' ? (hasPermission ? 'validationGreen' : 'red') : hasPermission ? 'success' : 'error';

  return (
    <Icon
      icon={hasPermission ? Icons.CHECK_MARK_CIRCLE : Icons.CLOSEROUND}
      color={iconColor}
      data-testid={testId}
      size="small"
    />
  );
};

export const PermissionsTable: FC<PermissionsTableProps> = ({
  handleBackToMain,
  config,
  employee,
  handleSubmit,
}) => {
  const { t } = useTranslation();
  const {
    get: { userPermissions, roleMatrix, currentStoreId, employees, profile },
    set,
  } = useUser();
  const { updateProfile } = useProfile();

  const [isEditing, setIsEditing] = useState(false);
  const [editablePermissions, setEditablePermissions] = useState<PermissionTableRow[]>([]);

  const currentUserRoles = useMemo(() => getSuamRoles(profile, currentStoreId), [profile, currentStoreId]);

  const initialPermissions = useMemo<AdditionalPermissions>(
    () => (employee ? JSON.parse(JSON.stringify(employee.additionalPermissions || {})) : {}),
    [employee],
  );

  const [permissions, setPermissions] = useState<AdditionalPermissions>(initialPermissions);
  const [savedPermissions, setSavedPermissions] = useState<AdditionalPermissions>(initialPermissions);

  const saveButtonEnabled = useMemo(() => {
    return JSON.stringify(permissions) !== JSON.stringify(savedPermissions);
  }, [permissions, savedPermissions]);

  useEffect(() => {
    if (isEditing) {
      setEditablePermissions(getTableData(permissionGroupings[config.type]));
    }
  }, [isEditing]);

  const hasRoleBasedPermission = (feature: string, actionKey: keyof typeof PERMISSION_TYPE_MAP) => {
    if (!employee) return false;

    const type = PERMISSION_TYPE_MAP[actionKey];

    // Get the EMPLOYEE's roles (not the current user's roles) to check if checkbox should be editable
    const employeeRoles = getSuamRoles(employee, currentStoreId);

    return employeeRoles.some((role) => {
      let employeeRole = isSupportedSuamRole(role) ? RoleMapping[role] : 'other';

      // We need to make an extra check here
      if (employeeRole === RoleMapping.EV) employeeRole = 'other';

      const featurePermissions = roleMatrix.box.featurePermissions[feature]?.permissions[employeeRole];

      return featurePermissions?.includes(type);
    });
  };

  const handleCheckBoxChange = (storeId: string, isChecked: boolean, feature: string, type: string) => {
    setPermissions((prev) => {
      const updated = JSON.parse(JSON.stringify(prev));

      if (!updated[storeId]) {
        updated[storeId] = {};
      }

      const existing = updated[storeId][feature]?.permissionType || '';
      const next = isChecked ? existing + type : existing.replace(type, '');

      if (!next) {
        delete updated[storeId][feature];
        if (Object.keys(updated[storeId]).length === 0) {
          delete updated[storeId];
        }
      } else {
        updated[storeId][feature] = {
          permissionType: removeDuplicateCharacters(next),
        };
      }

      return updated;
    });
  };

  const handlePermissionChange = (rowId: string, key: string, value: boolean) => {
    const type = PERMISSION_TYPE_MAP[key];
    if (type) {
      handleCheckBoxChange(currentStoreId, value, rowId, type);
    }
    // Also update the local state for immediate UI feedback
    setEditablePermissions((prev) => prev.map((p) => (p.id === rowId ? { ...p, [key]: value } : p)));
  };

  const getTableData = (keys: UserPermissionType[]): PermissionTableRow[] => {
    // Get the most up-to-date employee data from global state
    const currentEmployee = employee ? employees.find((emp) => emp.id === employee.id) || employee : employee;

    return keys
      .map((permissionType) => {
        let permission: Record<'canCreate' | 'canRead' | 'canUpdate' | 'canDelete', boolean>;

        if (permissionType === 'areaOverview') {
          const sourcePermissions = overviewWidgetsPermissionsArray.map((key) =>
            currentEmployee
              ? getEmployeePermissions(key, currentEmployee, roleMatrix, currentStoreId)
              : userPermissions?.[key],
          );

          permission = PERMISSION_ACTIONS.reduce((acc, { key }) => {
            acc[key] = sourcePermissions.some((perm) => perm?.[key]);
            return acc;
          }, {} as Record<'canCreate' | 'canRead' | 'canUpdate' | 'canDelete', boolean>);
        } else {
          const fetchedPermission = currentEmployee
            ? getEmployeePermissions(permissionType, currentEmployee, roleMatrix, currentStoreId)
            : userPermissions?.[permissionType];

          permission = PERMISSION_ACTIONS.reduce((acc, { key }) => {
            acc[key] = fetchedPermission?.[key] ?? false;
            return acc;
          }, {} as Record<'canCreate' | 'canRead' | 'canUpdate' | 'canDelete', boolean>);
        }

        const translationData = getPermissionTranslationData(permissionType);

        return {
          id: permissionType,
          name: translationData ? t(translationData.key, translationData.defaultValue) : permissionType,
          ...permission,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const getHeaderItems = (columnLabel: string): HeaderRowItem<string>[] => [
    {
      id: 'name',
      content: <Typography>{columnLabel}</Typography>,
      width: '200px',
    },
    ...PERMISSION_ACTIONS.map(({ key, label }) => ({
      id: key,
      content: <Typography>{label}</Typography>,
    })),
  ];

  const getRowItems = (item: PermissionTableRow) => [
    {
      id: 'name',
      content: <Typography data-testid={`${config.type}-name-${item.id}`}>{item.name}</Typography>,
    },
    ...PERMISSION_ACTIONS.map(({ key }) => {
      const isRolePermission = hasRoleBasedPermission(item.id, key);

      return {
        id: key,
        content: isEditing ? (
          <Checkbox
            isChecked={item[key]}
            onChange={(checked) => handlePermissionChange(item.id, key, checked)}
            size="small"
            isDisabled={isRolePermission || isEditPermissionDisabled(item.id)}
          />
        ) : (
          <PermissionIcon
            hasPermission={item[key]}
            testId={`${key.replace('can', '').toLowerCase()}-${item.id}`}
          />
        ),
      };
    }),
  ];

  // Save changes to backend and update global state
  const handleSave = async () => {
    if (!employee) return;

    if (!employee.additionalPermissions) {
      try {
        await updateProfile(employee.upn, [
          {
            op: 'add',
            path: `/additionalPermissions`,
            value: {},
          },
        ]);
      } catch (err) {
        console.log(err);
      }
    }

    const response = await updateProfile(employee.upn, [
      {
        op: 'add',
        path: `/additionalPermissions/${currentStoreId}`,
        value: {
          ...permissions[currentStoreId],
        },
      },
    ]);

    const successfulUpdate = !isFailureResponse(response);

    if (successfulUpdate) {
      set({
        employees: employees.map((currentEmployee) =>
          currentEmployee.id === employee.id ? response.data : currentEmployee,
        ),
      });

      setSavedPermissions(permissions);

      setIsEditing(false);
      setEditablePermissions([]);
    } else {
      setPermissions(savedPermissions);
    }

    handleSubmit?.(successfulUpdate, 'editPermissions');
  };

  // Check edit permissions
  const canEditPermissions = canEditEmployeePermissions(
    employee,
    profile,
    currentUserRoles,
    roleMatrix,
    currentStoreId,
  );

  return (
    <div data-testid={`${config.type}-permissions-view`}>
      <IconWithText
        label={t(commonTranslations.back.key, commonTranslations.back.defaultValue)}
        onClick={handleBackToMain}
        iconOptions={{ icon: Icons.ARROW, size: 'small', rotate: 'up' }}
        textOptions={{ type: 'body3' }}
        labelPosition="right"
        data-testid="back-to-permissions"
        position="left"
        padding={[4, 4, 0, 4]}
      />

      <ContainerV2 hasBorder borderRadius={[4]} padding={[4]} margin={[4]}>
        <IconWithText
          label={t(config.titleKey, config.titleDefault)}
          iconOptions={{ icon: Icons[config.icon], size: 'small' }}
          textOptions={{ type: 'h4' }}
        />

        <ContainerV2 maxHeight="380px" overflow="auto" display="flex" flex={1} minHeight={[0]} padding={[2, 0, 0]}>
          {config.type === 'widgets' ? (
            <ColumnFlexContainer gap={[4]} width="100%">
              <Table
                stickyHeader
                stickyFirstColumn
                items={getTableData(permissionGroupings.widgets)}
                headerItems={getHeaderItems(
                  t(
                    permissionsTranslations.widgetsColumnLabel.key,
                    permissionsTranslations.widgetsColumnLabel.defaultValue,
                  ),
                )}
                getRowItems={getRowItems}
              />
              <Table
                stickyHeader
                stickyFirstColumn
                items={getTableData(permissionGroupings.overviewWidgets)}
                headerItems={getHeaderItems(
                  t(
                    permissionsTranslations.overViewWidgetsColumnLabel.key,
                    permissionsTranslations.overViewWidgetsColumnLabel.defaultValue,
                  ),
                )}
                getRowItems={getRowItems}
              />
            </ColumnFlexContainer>
          ) : (
            <Table
              stickyHeader
              stickyFirstColumn
              items={isEditing ? editablePermissions : getTableData(permissionGroupings[config.type])}
              headerItems={getHeaderItems(config.columnLabel)}
              getRowItems={getRowItems}
              data-testid={`${config.type}-permissions-table`}
            />
          )}
        </ContainerV2>

        {employee &&
          canEditPermissions &&
          (!isEditing ? (
            <ContainerV2 margin={[4, 0, 0]}>
              <Button
                label={t(permissionsTranslations.editAccess.key, permissionsTranslations.editAccess.defaultValue)}
                variant="primary"
                onClick={() => setIsEditing(true)}
                buttonContentProps={{
                  iconOptions: { icon: Icons.EDIT, size: 'small' },
                }}
                fullWidth
              />
            </ContainerV2>
          ) : (
            <RowFlexContainer gap={[4]} margin={[4, 0, 0]}>
              <Button
                label={t(commonTranslations.cancel.key, commonTranslations.cancel.defaultValue)}
                variant="secondary"
                onClick={() => {
                  setIsEditing(false);
                  setPermissions(savedPermissions);
                }}
                buttonContentProps={{
                  iconOptions: { icon: Icons.CLOSE, size: 'small' },
                }}
                fullWidth
              />

              <Button
                label={t(
                  permissionsTranslations.updateAccess.key,
                  permissionsTranslations.updateAccess.defaultValue,
                )}
                variant="primary"
                onClick={handleSave}
                buttonContentProps={{
                  iconOptions: { icon: Icons.CHECK_MARK, size: 'small' },
                }}
                fullWidth
                disabled={!saveButtonEnabled}
              />
            </RowFlexContainer>
          ))}
      </ContainerV2>
    </div>
  );
};
